import { RefObject, useEffect } from 'react';

const resetMotion = (root: HTMLElement) => {
  root.style.setProperty('--motion-y-soft', '0px');
  root.style.setProperty('--motion-y-mid', '0px');
  root.style.setProperty('--motion-y-deep', '0px');
  root.style.setProperty('--motion-y-forward', '0px');
  root.style.setProperty('--motion-pointer-x', '0px');
  root.style.setProperty('--motion-pointer-y', '0px');
  root.style.setProperty('--motion-pointer-x-invert', '0px');
  root.style.setProperty('--motion-pointer-y-invert', '0px');
  root.style.setProperty('--motion-rotate-soft', '0deg');
  root.style.setProperty('--motion-tilt', '0deg');
  root.style.setProperty('--motion-progress', '0');
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export const useParallaxMotion = <T extends HTMLElement>(
  rootRef: RefObject<T>
) => {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof window === 'undefined') return;

    const reducedMotion = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

    let raf = 0;
    let pointerX = 0;
    let pointerY = 0;

    const update = () => {
      raf = 0;

      if (reducedMotion?.matches) {
        resetMotion(root);
        return;
      }

      const scrollY = window.scrollY || window.pageYOffset || 0;
      const viewportHeight = window.innerHeight || 1;
      const viewportWidth = window.innerWidth || 1;
      const pageHeight = document.documentElement.scrollHeight || viewportHeight;
      const maxScroll = Math.max(pageHeight - viewportHeight, 1);
      const progress = clamp(scrollY / maxScroll, 0, 1);

      root.style.setProperty('--motion-y-soft', `${scrollY * -0.025}px`);
      root.style.setProperty('--motion-y-mid', `${scrollY * -0.05}px`);
      root.style.setProperty('--motion-y-deep', `${scrollY * -0.085}px`);
      root.style.setProperty('--motion-y-forward', `${scrollY * 0.035}px`);
      root.style.setProperty('--motion-pointer-x', `${pointerX * 24}px`);
      root.style.setProperty('--motion-pointer-y', `${pointerY * 18}px`);
      root.style.setProperty('--motion-pointer-x-invert', `${pointerX * -20}px`);
      root.style.setProperty('--motion-pointer-y-invert', `${pointerY * -14}px`);
      root.style.setProperty(
        '--motion-rotate-soft',
        `${pointerX * 2.4 + progress * 2}deg`
      );
      root.style.setProperty('--motion-tilt', `${pointerX * -3}deg`);
      root.style.setProperty('--motion-progress', `${progress}`);

      if (viewportWidth < 820) {
        root.style.setProperty('--motion-pointer-x', `${pointerX * 10}px`);
        root.style.setProperty('--motion-pointer-y', `${pointerY * 8}px`);
      }
    };

    const requestUpdate = () => {
      if (!raf) raf = window.requestAnimationFrame(update);
    };

    const handleScroll = () => requestUpdate();
    const handleResize = () => requestUpdate();
    const handlePointerMove = (event: PointerEvent) => {
      pointerX = event.clientX / (window.innerWidth || 1) - 0.5;
      pointerY = event.clientY / (window.innerHeight || 1) - 0.5;
      requestUpdate();
    };

    update();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    window.addEventListener('pointermove', handlePointerMove, { passive: true });

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('pointermove', handlePointerMove);
      resetMotion(root);
    };
  }, [rootRef]);
};
